const TELEMETRY_MUTATION = `
  mutation TrackTelemetryEvent($input: TelemetryEventInput!) {
    trackTelemetryEvent(input: $input)
  }
`;

const DEFAULT_SAMPLE_RATES = {
  error: 1,
  warning: 1,
  info: 1,
  screen: 0,
  performance: 0.1,
};

const blockedKeys = ['email', 'password', 'token', 'secret', 'authorization', 'accessToken', 'idToken'];

let config = {
  endpoint: '',
  getAccessToken: null,
  sampleRates: DEFAULT_SAMPLE_RATES,
  enabled: process.env.NODE_ENV === 'production',
};

/**
 * Normalizes properties.
 *
 * @param {object} properties - properties value.
 * @returns {*} The derived operation result.
 */
function sanitizeProperties(properties = {}) {
  return Object.fromEntries(
    Object.entries(properties || {})
      .filter(([key]) => !blockedKeys.some((blocked) => key.toLowerCase().includes(blocked.toLowerCase())))
      .slice(0, 30)
      .map(([key, value]) => {
        if (value === null || value === undefined) return [key, null];
        if (['string', 'number', 'boolean'].includes(typeof value)) return [key, value];
        if (Array.isArray(value)) return [key, value.slice(0, 10).map((item) => String(item).slice(0, 160))];
        return [key, '[object]'];
      })
  );
}

/**
 * Determines whether sample.
 *
 * @param {*} severity - severity value.
 * @param {*} category - category value.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
function shouldSample(severity, category) {
  const rates = config.sampleRates || DEFAULT_SAMPLE_RATES;
  const rate = rates[category] ?? rates[severity] ?? 1;
  return Math.random() <= rate;
}

/**
 * Performs telemetry.
 *
 * @param {object} nextConfig - next config value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function configureTelemetry(nextConfig = {}) {
  config = {
    ...config,
    ...nextConfig,
    sampleRates: {
      ...DEFAULT_SAMPLE_RATES,
      ...(nextConfig.sampleRates || {}),
    },
  };
}

/**
 * Records telemetry.
 *
 * @param {string} name - name value.
 * @param {object} properties - properties value.
 * @param {object} options - Behavioral options for the operation.
 * @returns {Promise<*>} A promise resolving to the completed operation result.
 */
export async function trackTelemetry(name, properties = {}, options = {}) {
  const severity = options.severity || 'info';
  const category = options.category || name.split('.')[0];
  if (!config.enabled || !config.endpoint || !config.getAccessToken || !shouldSample(severity, category)) {
    return;
  }

  try {
    const accessToken = await config.getAccessToken();
    if (!accessToken) return;
    await fetch(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        query: TELEMETRY_MUTATION,
        variables: {
          input: {
            name,
            severity,
            platform: 'web',
            properties: {
              ...sanitizeProperties(properties),
              path: window.location.pathname,
              appVersion: process.env.REACT_APP_VERSION || 'web',
            },
          },
        },
      }),
      keepalive: true,
    });
  } catch {
    // Telemetry must never affect user flows.
  }
}

/**
 * Records client error.
 *
 * @param {string} name - name value.
 * @param {*} error - Failure being normalized or reported.
 * @param {object} properties - properties value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function trackClientError(name, error, properties = {}) {
  trackTelemetry(name, {
    ...properties,
    message: error?.message || String(error || ''),
    code: error?.extensions?.code || error?.code || '',
    rateLimitSource: error?.extensions?.rateLimitSource || '',
    rateLimitScope: error?.extensions?.rateLimitScope || '',
    rateLimitProvider: error?.extensions?.rateLimitProvider || '',
  }, { severity: 'error' });
}
